import { useCallback, useMemo, useState } from 'react';

import type { MouseEvent } from 'react';
import type { UseBasicPopoverResponse, UsePopoverResponse } from './types';

/**
 * 특정 컴포넌트를 anchor로 설정하여 Popover 컴포넌트를 제어하기 위해 필요한 요소를 반환하는 커스텀 훅
 *
 * @template {HTMLElement} [T=HTMLElement] anchor로 설정할 컴포넌트의 HTMLElement 타입을 설정한다.
 * @returns {UsePopoverResponse<T>}
 */
const usePopover = <T extends HTMLElement = HTMLElement>(): UsePopoverResponse<T> => {
  const [anchorEl, setAnchorEl] = useState<T | null>(null);

  const onOpen = useCallback((anchor: T) => {
    setAnchorEl(anchor);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  return {
    anchorEl,
    setAnchorEl,
    onOpen,
    onClose,
    open,
  };
};

/**
 * 특정 컴포넌트를 anchor로 설정하고 click event로 Popover 컴포넌트를 제어하기 위해 필요한 요소를 반환하는 커스텀 훅
 *
 * @template {HTMLElement} [T=HTMLElement] anchor로 설정할 컴포넌트의 HTMLElement 타입을 설정한다.
 * @returns {UseBasicPopoverResponse<T>}
 */
const useBasicPopover = <T extends HTMLElement = HTMLElement>(): UseBasicPopoverResponse<T> => {
  const { onOpen, open, ...others } = usePopover<T>();
  const id = useMemo(() => (open ? 'popover' : undefined), [open]);

  const onClick = useCallback(
    (event: MouseEvent<T>) => {
      onOpen(event.currentTarget);
    },
    [onOpen],
  );

  return { ...others, onOpen, open, id, onClick };
};

export { usePopover, useBasicPopover };
